import React, {
  useCallback,
  useRef,
  useMemo,
  useState,
  useContext,
  useEffect,
} from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import { Icon } from 'js/components';
import { faEllipsis } from '@fortawesome/pro-regular-svg-icons';
import {
  faCaretRight,
  faCaretDown,
  faCircleSmall,
} from '@fortawesome/pro-solid-svg-icons';
import { TreeContext } from '../TreeContext';
import styles from './CapabilityTree.module.scss';
import CapabilityForm from './CapabilityForm';
import { useStandardCollator } from 'js/utils/string';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import {
  attachInstruction,
  extractInstruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { delay } from 'js/utils/delay';
import { sortCapabilities } from '../ordering';

const INDENTATION = 20;

function CapabilityDragPreview({ capability }) {
  return (
    <div className={styles['drag-preview']}>
      <div className={styles['capability-name']}>{capability.name}</div>
      {capability.description && (
        <div className={styles['capability-description']}>
          {capability.description}
        </div>
      )}
    </div>
  );
}

function TreeDropLine({ instruction }) {
  if (!instruction) {
    return null;
  }

  switch (instruction.type) {
    case 'reorder-above':
    case 'reorder-below':
      return (
        <div
          className={classNames(styles['drop-line'], {
            [styles.top]: instruction.type === 'reorder-above',
            [styles.bottom]: instruction.type === 'reorder-below',
          })}
          style={{ left: instruction.currentLevel * INDENTATION }}
        />
      );

    case 'reparent':
      return (
        <div
          className={classNames(styles['drop-line'], styles.bottom)}
          style={{ left: instruction.desiredLevel * INDENTATION }}
        />
      );

    default:
      return null;
  }
}

function CapabilityNode({
  path,
  capability,
  isLastInGroup = false,
  onAddChild,
  onEdit,
  onSortChildren,
  className,
}) {
  const depth = path.length;
  const paddingLeft = depth * INDENTATION;
  const childPath = [...path, capability];

  const [showAddChild, setShowAddChild] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const nameRef = useRef(null);

  const { find, dispatch, uniqueContextId } = useContext(TreeContext);
  const nodeTreeState = find(capability.id);
  const showChildren = nodeTreeState?.isOpen;

  const [instruction, setInstruction] = useState(null);

  const handleAddChild = useCallback(async () => {
    await onAddChild(capability, { name: nameRef.current.value });
    nameRef.current.value = '';
  }, [onAddChild, capability]);

  const childrenCount = capability.children.length;
  const collator = useStandardCollator();
  const sortedChildren = useMemo(
    () => sortCapabilities(capability.children, collator),
    [capability.children, collator]
  );

  const toggleChildren = useCallback(() => {
    if (childrenCount > 0) {
      dispatch({ type: 'toggle', itemId: capability.id });
    }
  }, [dispatch, capability.id, childrenCount]);

  const treeIconProps = useMemo(() => {
    if (childrenCount === 0) {
      return { icon: faCircleSmall, transform: 'shrink-9' };
      // return { icon: faCircleSmall };
    }

    return showChildren ? { icon: faCaretDown } : { icon: faCaretRight };
  }, [showChildren, childrenCount]);

  const dragRef = useRef(null);
  const cancelExpandRef = useRef(null);
  const cancelExpand = useCallback(() => {
    cancelExpandRef.current?.();
    cancelExpandRef.current = null;
  }, []);

  useEffect(() => {
    if (!dragRef.current) return;

    return combine(
      draggable({
        element: dragRef.current,
        getInitialData: () => ({
          type: 'capability',
          uniqueContextId: uniqueContextId,
          isOpenOnDragStart: showChildren,
          capability,
        }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => {
          // A separate preview beside the pointer: the browser cancels the
          // drag when the pointer isn't over the drag image
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({ x: '12px', y: '8px' }),
            render({ container }) {
              ReactDOM.render(
                <CapabilityDragPreview capability={capability} />,
                container
              );
              return () => ReactDOM.unmountComponentAtNode(container);
            },
          });
        },
        onDragStart: ({ source }) => {
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: 'collapse', itemId: capability.id });
          }
        },
        onDrop: ({ source }) => {
          if (source.data.isOpenOnDragStart) {
            dispatch({ type: 'expand', itemId: capability.id });
          }
        },
      }),
      dropTargetForElements({
        element: dragRef.current,
        getData: ({ input, element }) => {
          return attachInstruction(
            {
              type: 'capability',
              uniqueContextId: uniqueContextId,
              capability,
            },
            {
              input,
              element,
              currentLevel: depth,
              indentPerLevel: INDENTATION,
              mode:
                showChildren && childrenCount > 0
                  ? 'expanded'
                  : isLastInGroup
                  ? 'last-in-group'
                  : 'standard',
            }
          );
        },
        canDrop: ({ source }) => {
          const sourceId = source.data.capability?.id;

          return (
            source.data.type === 'capability' &&
            source.data.uniqueContextId === uniqueContextId &&
            sourceId !== capability.id &&
            !path.some((ancestor) => ancestor.id === sourceId)
          );
        },
        onDrag: ({ self }) => {
          const nextInstruction = extractInstruction(self.data);
          setInstruction(nextInstruction);

          if (
            nextInstruction?.type === 'make-child' &&
            childrenCount &&
            !nodeTreeState?.isOpen &&
            !cancelExpandRef.current
          ) {
            cancelExpandRef.current = delay({
              waitMs: 500,
              fn: () => dispatch({ type: 'expand', itemId: capability.id }),
            });
          }
        },
        onDragLeave: () => {
          setInstruction(null);
          cancelExpand();
        },
        onDrop: () => {
          setInstruction(null);
          cancelExpand();
        },
      })
    );
  }, [
    dragRef,
    uniqueContextId,
    capability,
    path,
    depth,
    isLastInGroup,
    showChildren,
    cancelExpand,
    childrenCount,
    dispatch,
    nodeTreeState?.isOpen,
  ]);

  const nodeClassName = useMemo(
    () =>
      classNames(styles.capability, className, {
        [styles['drop-target']]: instruction?.type === 'make-child',
      }),
    [instruction, className]
  );

  return (
    <>
      <div className={nodeClassName}>
        <TreeDropLine instruction={instruction} />
        {showChildren && childrenCount > 0 && (
          <div
            className={styles['partial-gutter']}
            style={{ left: `calc(0.75rem + ${paddingLeft}px)` }}
          />
        )}

        {/* <DepthGutters depth={depth} /> */}
        <button
          style={{ paddingLeft }}
          className={styles['tree-item']}
          onClick={toggleChildren}
          ref={dragRef}
        >
          <span className={styles['capability-body']}>
            <span className={styles['child-toggle']}>
              <Icon size="sm" fixedWidth {...treeIconProps} />
            </span>
            <span className={styles['capability-body-content']}>
              <span className={styles['capability-name']}>
                {capability.name}
              </span>
              <span className={styles['capability-description']}>
                {capability.description}
              </span>
            </span>
          </span>
        </button>
        <div className={styles['capability-menu']}>
          <Dropdown isOpen={showMenu} toggle={() => setShowMenu(!showMenu)}>
            <DropdownToggle color="tree-menu">
              <Icon icon={faEllipsis} />
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem onClick={() => onAddChild([...path, capability])}>
                Add capability
              </DropdownItem>
              <DropdownItem onClick={() => onEdit(path, capability)}>
                Edit
              </DropdownItem>
              {childrenCount > 1 && (
                <DropdownItem
                  onClick={() => onSortChildren(capability.children)}
                >
                  Sort
                </DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
      {showAddChild && (
        <div
          style={{ paddingLeft: (depth + 1) * INDENTATION }}
          className={styles['add-capability']}
        >
          <div
            className={styles.gutter}
            style={{ left: `calc(0.75rem + ${paddingLeft}px)` }}
          />
          <CapabilityForm
            capability={capability}
            onSubmit={handleAddChild}
            onCancel={() => setShowAddChild(false)}
          />
        </div>
      )}
      {showChildren && (
        <div className={styles['children']}>
          <div
            className={styles.gutter}
            style={{ left: `calc(0.75rem + ${paddingLeft}px)` }}
          />
          {sortedChildren.map((child, index) => (
            <CapabilityNode
              key={child.id}
              path={childPath}
              capability={child}
              isLastInGroup={index === sortedChildren.length - 1}
              onAddChild={onAddChild}
              onEdit={onEdit}
              onSortChildren={onSortChildren}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default CapabilityNode;
